/**
 * Arrange logic — geometry + Mapping cam-file I/O.
 * Unit space (shared with stadium): origin center, +x right, +y down, angle ° CW.
 *
 * Blueprint rule: Arrange is the source of truth. Preview must use the same
 * extents, scale, and positions (buildBlueprint) — tiles only paint inside.
 */

import {
  fetchCamMapJson,
  listMappingCams,
  uploadCamMapJson,
} from '../mapLogic';

export const SEED = 240;
export const LAYOUT_SCALE = 0.55;
export const ANGLE_STEP = 45;
export const MAX_EDGE = 200;
export const MIN_EDGE = 48;
export const PLACEHOLDER = 64;

const SEED_SLOTS = [
  [0, -1], [1, 0], [0, 1], [-1, 0],
  [1, -1], [-1, -1], [1, 1], [-1, 1],
];

export const COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#f97316', '#ec4899', '#84cc16', '#6366f1',
];

export const toPx = (u) => Math.round(Number(u) * SEED);
export const toUnit = (px) => Number(px) / SEED;

export const layoutToCss = (x = 0, y = 0, angle = 0) => ({
  left: `${50 + Number(x) * LAYOUT_SCALE * 50}%`,
  top: `${50 + Number(y) * LAYOUT_SCALE * 50}%`,
  transform: `translate(-50%, -50%) rotate(${Number(angle) || 0}deg)`,
});

/** Exact cam:regionId, else same-cam id suffix (handles rename drift). */
export const resolveArrangeLayout = (map, cam, rid) => {
  const layoutMap = map || {};
  if (!cam || !rid) return null;
  const exact = layoutMap[`${cam}:${rid}`];
  if (exact) return exact;
  const i = String(rid).lastIndexOf('-');
  const tail = i >= 0 ? String(rid).slice(i + 1) : '';
  if (tail.length < 4) return null;
  const prefix = `${cam}:`;
  const key = Object.keys(layoutMap).find((k) => {
    const id = k.startsWith(prefix) ? k.slice(prefix.length) : '';
    return id.includes('-') && id.endsWith(`-${tail}`);
  });
  return key ? layoutMap[key] : null;
};

export const snapAngle = (deg) => {
  const n = ((deg % 360) + 360) % 360;
  return (Math.round(n / ANGLE_STEP) * ANGLE_STEP) % 360;
};

export const normalizeCam = (camera) => {
  if (!camera) return null;
  if (typeof camera === 'object') return normalizeCam(camera.value || camera.label);
  const s = String(camera).trim().toLowerCase();
  if (/^cam\d+$/.test(s)) return s;
  const m = /(\d+)/.exec(s);
  return m ? `cam${m[1]}` : null;
};

/** Prefer tileWidth/tileHeight; else derive from legacy mosaic width/height ÷ cols/rows. */
const resolveTileSize = (r = {}) => {
  const cols = Number(r.cols);
  const rows = Number(r.rows);
  let tileWidth = Number(r.tileWidth);
  let tileHeight = Number(r.tileHeight);
  if (!(tileWidth > 0) && cols > 0 && Number(r.width) > 0) {
    tileWidth = Number(r.width) / cols;
  }
  if (!(tileHeight > 0) && rows > 0 && Number(r.height) > 0) {
    tileHeight = Number(r.height) / rows;
  }
  return {
    tileWidth: tileWidth > 0 ? tileWidth : null,
    tileHeight: tileHeight > 0 ? tileHeight : null,
  };
};

/**
 * Mosaic extent from tile size × grid.
 * Accepts tileWidth/tileHeight, or legacy mosaic width/height ÷ cols/rows.
 */
export const regionExtent = (r) => {
  const cols = Number(r.cols);
  const rows = Number(r.rows);
  if (!(cols > 0 && rows > 0)) return null;
  const { tileWidth, tileHeight } = resolveTileSize(r);
  if (!(tileWidth > 0 && tileHeight > 0)) return null;
  return { w: cols * tileWidth, h: rows * tileHeight };
};

/** Scale mosaic size into blueprint px — keep exact aspect (no per-axis rounding). */
const displaySize = (w, h, scale) => {
  let dw = Math.max(0, w) * scale;
  let dh = Math.max(0, h) * scale;
  const minDim = Math.min(dw, dh);
  if (minDim > 0 && minDim < MIN_EDGE) {
    const bump = MIN_EDGE / minDim;
    dw *= bump;
    dh *= bump;
  }
  return { width: dw, height: dh };
};

const rotatedCorners = (cx, cy, width, height, angleDeg = 0) => {
  const rad = (Number(angleDeg) || 0) * (Math.PI / 180);
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const hw = width / 2;
  const hh = height / 2;
  return [
    [-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh],
  ].map(([dx, dy]) => ({
    x: cx + dx * cos - dy * sin,
    y: cy + dx * sin + dy * cos,
  }));
};

/**
 * Single blueprint for Arrange + Crowd Count stadium.
 * Origin at center; block x/y are center offsets (same as Arrange canvas).
 */
export const buildBlueprint = (raw = []) => {
  const indexed = (raw || []).map((r, i) => ({ r, i, ext: regionExtent(r) }));
  const list = indexed.filter((x) => x.ext);
  if (!list.length) {
    return {
      blocks: [], slots: [], stageW: 0, stageH: 0, scale: 0, maxEdge: 0,
    };
  }

  const maxEdge = Math.max(...list.flatMap(({ ext }) => [ext.w, ext.h]));
  const scale = maxEdge > 0 ? MAX_EDGE / maxEdge : 0;

  const blocks = list.map(({ r, i, ext }) => {
    const layout = r.arrangeLayout;
    const saved = layout && Number.isFinite(layout.x) && Number.isFinite(layout.y);
    const [sx, sy] = SEED_SLOTS[i % SEED_SLOTS.length];
    const { width, height } = displaySize(ext.w, ext.h, scale);
    const x = saved ? toPx(layout.x) : Math.round(sx * SEED);
    const y = saved ? toPx(layout.y) : Math.round(sy * SEED);
    const angle = saved ? snapAngle(layout.angle || 0) : 0;
    return {
      id: r.id,
      camIdx: r.camIdx,
      regionId: r.regionId,
      label: r.label,
      masks: r.masks || [],
      color: COLORS[i % COLORS.length],
      width,
      height,
      x,
      y,
      angle,
      extent: ext,
    };
  });

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  blocks.forEach((b) => {
    rotatedCorners(b.x, b.y, b.width, b.height, b.angle).forEach(({ x, y }) => {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    });
  });

  const stageW = Math.max(0, maxX - minX);
  const stageH = Math.max(0, maxY - minY);
  const slots = blocks.map((b) => ({
    ...b,
    // Stage-local center for stadium (top-left origin).
    left: b.x - minX,
    top: b.y - minY,
  }));

  return {
    blocks, slots, stageW, stageH, scale, maxEdge, minX, minY,
  };
};

export const clampPos = (region, canvas, patch = {}) => {
  const next = { ...region, ...patch };
  const { width = 0, height = 0 } = canvas?.getBoundingClientRect?.() || {};
  const maxX = Math.max(0, width / 2 - (next.width || PLACEHOLDER) / 2);
  const maxY = Math.max(0, height / 2 - (next.height || PLACEHOLDER) / 2);
  return {
    ...next,
    x: Math.round(Math.min(maxX, Math.max(-maxX, next.x))),
    y: Math.round(Math.min(maxY, Math.max(-maxY, next.y))),
  };
};

export const toBlocks = (raw, canvas) => (
  buildBlueprint(raw).blocks.map((b) => clampPos(b, canvas))
);

export const loadRegions = async () => {
  const cams = await listMappingCams();
  const out = [];
  await Promise.all(cams.map(async (camIdx) => {
    try {
      const regions = (await fetchCamMapJson(camIdx))?.scan?.regions;
      if (!Array.isArray(regions)) return;
      regions.forEach((region) => {
        if (!region?.regionId) return;
        const regionId = String(region.regionId);
        const name = String(region.name || '').trim();
        out.push({
          id: `${camIdx}:${regionId}`,
          camIdx,
          regionId,
          name,
          label: `${camIdx} · ${name || regionId}`,
          start: { pan: region.start?.pan, tilt: region.start?.tilt },
          end: { pan: region.end?.pan, tilt: region.end?.tilt },
          rows: region.rows ?? null,
          cols: region.cols ?? null,
          ...resolveTileSize(region),
          arrangeLayout: region.arrangeLayout || null,
          masks: region.masks || [],
        });
      });
    } catch (err) {
      console.warn(`Arrange: failed to load ${camIdx}`, err);
    }
  }));
  return out.sort((a, b) => (
    a.camIdx.localeCompare(b.camIdx) || a.regionId.localeCompare(b.regionId)
  ));
};

/** Pose + size meta from cam.json (stadium needs sizes; Arrange needs pose). */
export const loadArrangeLayoutMap = async () => {
  const map = {};
  (await loadRegions()).forEach((r) => {
    const L = r.arrangeLayout;
    const hasPose = L && Number.isFinite(Number(L.x)) && Number.isFinite(Number(L.y));
    const meta = {
      ...(hasPose ? {
        x: Number(L.x),
        y: Number(L.y),
        angle: Number(L.angle) || 0,
      } : {}),
      tileWidth: r.tileWidth ?? null,
      tileHeight: r.tileHeight ?? null,
      rows: r.rows ?? null,
      cols: r.cols ?? null,
    };
    if (!hasPose && !regionExtent(meta)) return;
    map[r.id] = meta;
    if (r.name) map[`${r.camIdx}:${r.name}`] = meta;
  });
  return map;
};

export const saveLayouts = async (regions = []) => {
  const byCam = new Map();
  regions.forEach((r) => {
    const cam = normalizeCam(r.camIdx);
    if (!cam || !r.regionId) return;
    if (!byCam.has(cam)) byCam.set(cam, new Map());
    byCam.get(cam).set(String(r.regionId), {
      x: toUnit(r.x),
      y: toUnit(r.y),
      angle: snapAngle(r.angle),
    });
  });

  const results = await Promise.all([...byCam].map(async ([camIdx, wanted]) => {
    try {
      const data = await fetchCamMapJson(camIdx);
      if (!data?.scan) return { camIdx, ok: false, error: 'No scan section' };
      let patched = 0;
      data.scan.regions = (data.scan.regions || []).map((region) => {
        const arrangeLayout = wanted.get(String(region.regionId));
        if (!arrangeLayout) return region;
        patched += 1;
        return { ...region, arrangeLayout };
      });
      if (!patched) return { camIdx, ok: false, error: 'No matching regionIds' };
      return { camIdx, ok: await uploadCamMapJson(camIdx, data) };
    } catch (err) {
      return { camIdx, ok: false, error: err?.message || 'Upload failed' };
    }
  }));

  return { ok: results.length > 0 && results.every((r) => r.ok), results };
};
